import intersection from "lodash/intersection";
import {
  Issue,
  IssueStatus,
  PermittedAction,
  ProjectMemberType,
} from "@adsk/acc-issues-types";
import { isAttributeEditable } from "../utils/data/entityPermissions";
import { useGetLevelOneUserShouldIncludeMemberFn } from "@adsk/acc-issues-common/hooks/data/useGetLevelOneUserShouldIncludeMemberFn";

export type PermittedBulkEditFieldsMap = Partial<Record<keyof Issue, boolean>>;

export interface BulkEditPermissions {
  permittedFieldsMap: PermittedBulkEditFieldsMap;
  permittedStatuses: IssueStatus[];
  canDeleteAllSelectedIssues: boolean;
  canAssignAllToAllSelectedIssues: boolean;
}

export const getPermissionsForBulkEdit = ({
  issues,
  watcherProp,
}: {
  issues: Issue[];
  watcherProp: "watchers" | "watcherObjects";
}): BulkEditPermissions => {
  const permittedFieldsMap: PermittedBulkEditFieldsMap = {
    title: true,
    status: true,
    issueTypeId: true,
    description: true,
    assignedTo: true,
    watchers: true,
    watcherObjects: true,
    locationId: true,
    locationDetails: true,
    customAttributes: true,
    published: true,
    startDate: true,
    dueDate: true,
    rootCauseId: true,
  };
  let permittedStatuses: IssueStatus[] = [
    IssueStatus.Draft,
    IssueStatus.Open,
    IssueStatus.Pending,
    IssueStatus.InProgress,
    IssueStatus.Completed,
    IssueStatus.InReview,
    IssueStatus.NotApproved,
    IssueStatus.InDispute,
    IssueStatus.Closed,
  ];
  let canDeleteAllSelectedIssues = true;
  let canAssignAllToAllSelectedIssues = true;
  const levelOneUserShouldIncludeMemberFn =
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useGetLevelOneUserShouldIncludeMemberFn();

  // we go through the issues in one pass, and we determine multiple things in that pass
  issues.forEach(issue => {
    Object.keys(permittedFieldsMap).forEach((fieldName: string) => {
      if (!isAttributeEditable(issue, fieldName as keyof Issue)) {
        permittedFieldsMap[fieldName as keyof Issue] = false;
      }
    });
    // the permitted statuses are whatever the least permitted issue in the bunch allows
    permittedStatuses = intersection(
      permittedStatuses,
      issue.permittedStatuses
    );
    // if any issue does not include a permitted action:
    // then the ability to perform that action in a bulk context should be false
    if (!issue.permittedActions.includes(PermittedAction.AssignAll)) {
      canAssignAllToAllSelectedIssues = false;
      let areAllMembersIncluded;
      if (watcherProp === "watchers") {
        areAllMembersIncluded = (issue[watcherProp] || []).every(watcherId =>
          levelOneUserShouldIncludeMemberFn({
            memberType: ProjectMemberType.User,
            value: watcherId,
          })
        );
      } else {
        areAllMembersIncluded = (issue[watcherProp] || []).every(
          watcherObject =>
            levelOneUserShouldIncludeMemberFn({
              memberType: ProjectMemberType.User,
              value: watcherObject.id,
            })
        );
      }

      permittedFieldsMap.watchers = areAllMembersIncluded;
      permittedFieldsMap.watcherObjects = areAllMembersIncluded;
    }
    if (!issue.permittedActions.includes(PermittedAction.Delete)) {
      canDeleteAllSelectedIssues = false;
    }
  });

  return {
    permittedFieldsMap,
    permittedStatuses,
    canDeleteAllSelectedIssues,
    canAssignAllToAllSelectedIssues,
  };
};

export const useShouldIncludeMemberFn = ({
  canAssignAllToAllSelectedIssues,
}: {
  canAssignAllToAllSelectedIssues: boolean;
}) => {
  const levelOneUserShouldIncludeMemberFn =
    useGetLevelOneUserShouldIncludeMemberFn();
  const shouldIncludeMemberFn = !canAssignAllToAllSelectedIssues
    ? levelOneUserShouldIncludeMemberFn
    : undefined;
  return shouldIncludeMemberFn;
};
